// Keyboard and touch input, turned into one input byte per simulation tick.
// Held keys are sampled each tick; dash and attack are "edges" that fire once on the tick after the press.
import { IN } from '../../../shared/sim.js';

const KEYS: Record<string, string> = {
  ArrowUp: 'up', KeyW: 'up', ArrowDown: 'down', KeyS: 'down', ArrowLeft: 'left', KeyA: 'left', ArrowRight: 'right', KeyD: 'right',
  ShiftLeft: 'run', ShiftRight: 'run', Space: 'jump', KeyX: 'attack', KeyJ: 'attack', KeyC: 'dash', KeyK: 'dash', KeyE: 'act', KeyZ: 'act', KeyR: 'mount',
};

export class Input {
  private held = new Set<string>();
  private edges = 0;
  runToggle = false;
  /** Set by the game while you're flying: then holding dash means "descend" rather than one dash. */
  flying = false;
  onAct: () => void = () => {};
  onAttackPress: () => boolean = () => false;   // return true to swallow (e.g. to advance dialogue)
  onChatKey: (slash: boolean) => void = () => {};
  onBagKey: () => void = () => {};
  onPartyKey: () => void = () => {};
  enabled = () => true;

  constructor() {
    addEventListener('keydown', e => {
      if (/INPUT|TEXTAREA|SELECT/.test((document.activeElement as HTMLElement)?.tagName || '')) return;
      if (e.code === 'Enter' || e.code === 'Slash') { e.preventDefault(); this.onChatKey(e.code === 'Slash'); return; }
      if ((e.code === 'KeyB' || e.code === 'KeyI') && !e.repeat) { e.preventDefault(); this.onBagKey(); return; }
      if (e.code === 'KeyP' && !e.repeat) { e.preventDefault(); this.onPartyKey(); return; }
      const k = KEYS[e.code]; if (!k) return;
      e.preventDefault();
      if (e.repeat && (k === 'attack' || k === 'dash' || k === 'act' || k === 'mount' || k === 'jump')) { if (k === 'jump') this.held.add(k); return; }
      if (k === 'act') return this.onAct();
      if (k === 'attack') { if (!this.onAttackPress()) this.edges |= IN.attack; return; }
      if (k === 'jump') { if (!this.onAttackPress()) { this.held.add('jump'); this.edges |= IN.jump; } return; }   // even a very quick tap jumps   // Space also turns the page in a conversation
      if (k === 'mount') { this.edges |= IN.mount; return; }
      if (k === 'dash') { this.edges |= IN.dash; this.held.add('dashHold'); return; }
      this.held.add(k);
    });
    addEventListener('keyup', e => { const k = KEYS[e.code]; if (k) { this.held.delete(k); if (k === 'dash') this.held.delete('dashHold'); } });
    addEventListener('blur', () => this.held.clear());
    document.querySelectorAll<HTMLElement>('.pad [data-k]').forEach(b => {
      const k = b.dataset.k!;
      b.addEventListener('pointerdown', e => {
        e.preventDefault(); try { b.setPointerCapture(e.pointerId); } catch { /* pointer already gone */ }
        if (k === 'act') this.onAct();
        else if (k === 'chat') this.onChatKey(false);
        else if (k === 'bag') this.onBagKey();
        else if (k === 'attack') { if (!this.onAttackPress()) this.edges |= IN.attack; }
        else if (k === 'dash') { this.edges |= IN.dash; this.held.add('dashHold'); }
        else if (k === 'mount') this.edges |= IN.mount;
        else if (k === 'jump') { if (!this.onAttackPress()) { this.held.add('jump'); this.edges |= IN.jump; } }
        else if (k === 'run') { this.runToggle = !this.runToggle; b.setAttribute('aria-pressed', String(this.runToggle)); }
        else this.held.add(k);
      });
      const up = () => { this.held.delete(k); if (k === 'dash') this.held.delete('dashHold'); };
      b.addEventListener('pointerup', up); b.addEventListener('pointercancel', up); b.addEventListener('lostpointercapture', up);
    });
  }

  /** The input byte for this tick. */
  sample(): number {
    if (!this.enabled()) { this.edges = 0; return 0; }
    let b = this.edges; this.edges = 0;
    const h = this.held;
    if (h.has('up')) b |= IN.up; if (h.has('down')) b |= IN.down; if (h.has('left')) b |= IN.left; if (h.has('right')) b |= IN.right;
    if (h.has('run') || this.runToggle) b |= IN.run;
    if (h.has('jump')) b |= IN.jump;
    if (this.flying && h.has('dashHold')) b |= IN.dash;   // flying: hold dash to come down
    return b;
  }
  clear(): void { this.held.clear(); this.edges = 0; }
}
